import { useEffect, useRef, useCallback } from 'react';
import { subscriptionService } from '../services/graphql/subscriptions';
import { Expense } from '../types/expense';
import { User, UserRole } from '../types/user';

/**
 * Custom hook for managing GraphQL subscriptions
 * Requirements: 9.3 - Real-time expense status updates with connection management
 */

interface UseExpenseSubscriptionOptions {
  user: User | null;
  onExpenseUpdate?: (expense: Expense) => void;
  onApprovalRequired?: (expense: Expense) => void;
  onError?: (error: any) => void;
  enabled?: boolean;
}

export const useExpenseSubscriptions = ({
  user,
  onExpenseUpdate,
  onApprovalRequired,
  onError,
  enabled = true
}: UseExpenseSubscriptionOptions) => {
  const unsubscribeFunctions = useRef<(() => void)[]>([]);
  const isSubscribed = useRef(false);

  const handleError = useCallback((error: any) => {
    console.error('Subscription error:', error);
    if (onError) {
      onError(error);
    }
  }, [onError]);

  const subscribe = useCallback(() => {
    if (!user || !enabled || isSubscribed.current) {
      return;
    }

    const unsubscribers: (() => void)[] = [];

    try {
      // Subscribe to expense status updates for the current user
      if (onExpenseUpdate) {
        const unsubscribeStatus = subscriptionService.subscribeToExpenseStatusUpdates(
          user.id,
          onExpenseUpdate,
          handleError
        );
        unsubscribers.push(unsubscribeStatus);
      }

      // Subscribe to approval notifications for managers and finance admins
      if (onApprovalRequired && (user.role === UserRole.MANAGER || user.role === UserRole.FINANCE_ADMIN)) {
        const unsubscribeApprovals = subscriptionService.subscribeToApprovalNotifications(
          user.id,
          onApprovalRequired,
          handleError
        );
        unsubscribers.push(unsubscribeApprovals);
      }

      // Subscribe to all expense updates for finance admins
      if (user.role === UserRole.FINANCE_ADMIN && onExpenseUpdate) {
        const unsubscribeAll = subscriptionService.subscribeToAllExpenseUpdates(
          onExpenseUpdate,
          handleError
        );
        unsubscribers.push(unsubscribeAll);
      }

      unsubscribeFunctions.current = unsubscribers;
      isSubscribed.current = true;

    } catch (error) {
      console.error('Failed to set up subscriptions:', error);
      handleError(error);
      
      // Clean up any partial subscriptions
      unsubscribers.forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (cleanupError) {
          console.error('Error during subscription cleanup:', cleanupError);
        }
      });
    }
  }, [user, enabled, onExpenseUpdate, onApprovalRequired, handleError]);

  const unsubscribe = useCallback(() => {
    if (!isSubscribed.current) {
      return;
    }

    unsubscribeFunctions.current.forEach(unsubscribeFunc => {
      try {
        unsubscribeFunc();
      } catch (error) {
        console.error('Error unsubscribing:', error);
      }
    });

    unsubscribeFunctions.current = [];
    isSubscribed.current = false;
  }, []);

  // Set up subscriptions when user or enabled state changes
  useEffect(() => {
    if (enabled && user) {
      subscribe();
    } else {
      unsubscribe();
    }

    return unsubscribe;
  }, [user, enabled, subscribe, unsubscribe]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  return {
    isSubscribed: isSubscribed.current,
    subscribe,
    unsubscribe,
    activeSubscriptionCount: subscriptionService.getActiveSubscriptionCount()
  };
};

/**
 * Hook specifically for expense list components
 */
export const useExpenseListSubscriptions = (
  user: User | null,
  onExpenseUpdate: (expense: Expense) => void,
  enabled: boolean = true
) => {
  return useExpenseSubscriptions({
    user,
    onExpenseUpdate,
    enabled
  });
};

/**
 * Hook specifically for approval queue components
 */
export const useApprovalQueueSubscriptions = (
  user: User | null,
  onApprovalRequired: (expense: Expense) => void,
  enabled: boolean = true
) => {
  return useExpenseSubscriptions({
    user,
    onApprovalRequired,
    enabled
  });
};

/**
 * Hook for dashboard components that need both types of updates
 */
export const useDashboardSubscriptions = (
  user: User | null,
  onExpenseUpdate: (expense: Expense) => void,
  onApprovalRequired: (expense: Expense) => void,
  enabled: boolean = true
) => {
  return useExpenseSubscriptions({
    user,
    onExpenseUpdate,
    onApprovalRequired,
    enabled
  });
};